import { SerializedStyles } from "@emotion/react";
import { Ref, memo, useCallback } from "react";
import { useNoSubmit } from "src/apps/AppFlow/pages/hooks";
import { styles } from "src/apps/AppFlow/pages/styles";
import { FormFlowPage, IllustrativeOptions, TextInput } from "src/components";
import { createStyles } from "src/styles";
import { AssetDetailsValue } from "src/types/Application";
import { noInput } from "src/utilities/input";

import { enums, formatters } from "@fraction/shared";

const { FINANCIAL_ASSET_TYPE_MAP } = formatters.enums;

export interface AssetDetailsProps {
  onChange: (value: AssetDetailsValue) => void;
  value?: AssetDetailsValue;
  title?: string;

  focusRef?: Ref<HTMLInputElement>;
  onNext: () => void;
  onBack: () => void;
  style?: SerializedStyles;
}

const OPTION_STYLES = createStyles({
  options: {
    marginBottom: "24px",
  },
});

const OPTIONS = [
  enums.FinancialAssetType.CASH_OR_SAVINGS,
  enums.FinancialAssetType.SHARES_OR_OTHER_INVESTMENTS,
  enums.FinancialAssetType.CRYPTOCURRENCY,
  enums.FinancialAssetType.OTHER,
];

const AssetDetails = ({
  onNext,
  onBack,
  style,
  focusRef,
  value,
  onChange,
  title,
  ...props
}: AssetDetailsProps) => {
  const validate = useCallback(() => !noInput(value?.value) && !noInput(value?.type), [value]);
  const { handleNext, nextDisabled } = useNoSubmit({ onNext, validate });

  const handleChangeDescription = useCallback(
    (description?: string) => {
      onChange({
        ...value,
        description,
      });
    },
    [onChange, value]
  );

  const handleChangeValue = useCallback(
    (assetValue?: string) => {
      onChange({
        ...value,
        value: assetValue,
      });
    },
    [onChange, value]
  );

  const handleChangeType = useCallback(
    (type?: enums.FinancialAssetType) => {
      onChange({
        ...value,
        type,
      });
    },
    [onChange, value]
  );

  return (
    <FormFlowPage
      {...props}
      title={title || "What is your asset?"}
      description="Tell us a little bit more about your asset."
      onNext={handleNext}
      onBack={onBack}
      style={style}
      nextDisabled={nextDisabled}
    >
      <IllustrativeOptions
        focusRef={focusRef}
        label="Asset type"
        placeholder="e.g Cash or savings"
        value={value?.type}
        onChange={handleChangeType}
        options={OPTIONS.map((type) => ({
          value: type,
          label: FINANCIAL_ASSET_TYPE_MAP[type],
        }))}
        containerStyle={OPTION_STYLES.options}
      />
      <TextInput
        multiline
        style={styles.topInput}
        name="assetDescription"
        label="Asset description"
        value={value?.description || ""}
        onChange={handleChangeDescription}
      />
      <TextInput
        style={styles.topInput}
        name="value"
        label="Asset value"
        value={value?.value || ""}
        onChange={handleChangeValue}
        maskOptions={{
          rightAlign: false,
          alias: "currency",
          prefix: "$ ",
          jitMasking: false,
          digits: 0,
        }}
      />
    </FormFlowPage>
  );
};

export default memo(AssetDetails);
